import React, {
  Children,
  HtmlHTMLAttributes,
  MutableRefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
  useContext,
} from "react";
import { FormContext } from "./index";
import clsx from "clsx";
import { Intent } from "../../common/enums";

export interface FormControlProps {
  label?: string;
  name: string;
  validationRules?: {
    rule: RegExp;
    message?: string;
    intent?: Intent;
  }[];
  required?: boolean;
  onChange?: ({
    name,
    value,
    isValid,
  }: {
    name: string;
    value: any;
    isValid: boolean;
  }) => void;
  defaultValue?: string | boolean | number | [];
  className?: string;
  children: React.ReactElement<any>;
  reset?: boolean;
}
/**
 *
 * A Form Control Element
 * defaultValue should not change  in component lifecycle
 */
export default function FormControl({
  label,
  validationRules,
  className,
  name,
  defaultValue,
  reset = false,
  onChange: onChange_,
  required = false,
  children,
}: FormControlProps) {
  const [value, setValue] = useState<string | boolean | number | any[]>(
    defaultValue ? defaultValue : "",
  );
  const [message, setMessage] = useState("");
  const [intent, setIntent] = useState<Intent>(Intent.MUTED);

  const context = useContext(FormContext);
  const actualOnChange = onChange_ || context?.handleChange;
  const actualReset = reset || context?.reset;

  useEffect(() => {
    if (actualReset) queueMicrotask(() => setValue(defaultValue ? defaultValue : ""));
  }, [defaultValue, actualReset]);

  useEffect(() => {
    if (actualOnChange) {
      actualOnChange({ name: name, value: value, isValid: intent !== Intent.ERROR });
    }
  }, [intent, name, actualOnChange, value]);

  const validate = useCallback(
    (value__: string) => {
      const message_ = validationRules?.find((rule) => {
        return !required && value__.length === 0
          ? false
          : !value__.match(rule.rule);
      });

      setMessage(message_?.message ?? "");
      if (message_) {
        setIntent(message_.intent ?? Intent.ERROR);
        if (message_.intent === Intent.ERROR) return false;
      } else {
        !value__ ? setIntent(Intent.MUTED) : setIntent(Intent.SUCCESS);
      }
      return true;
    },
    [validationRules, required],
  );

  // Validate a File object (used for <input type="file"> / profile picture uploads).
  // Runs the same validationRules against the file's name instead of the raw
  // input value, since browsers mask the real path as "C:\fakepath\<name>".
  const validateFile = useCallback(
    (file: File | null) => {
      if (!file) {
        // No file selected
        if (required) {
          const requiredRule = validationRules?.[0];
          setMessage(requiredRule?.message ?? "This field is required");
          setIntent(Intent.ERROR);
          return false;
        }
        setMessage("");
        setIntent(Intent.MUTED);
        return true;
      }

      const message_ = validationRules?.find(
        (rule) => !file.name.match(rule.rule),
      );

      setMessage(message_?.message ?? "");
      if (message_) {
        setIntent(message_.intent ?? Intent.ERROR);
        if (message_.intent === Intent.ERROR) return false;
      } else {
        setIntent(Intent.SUCCESS);
      }
      return true;
    },
    [validationRules, required],
  );

  useEffect(() => {
    if (defaultValue && typeof defaultValue === "string")
      queueMicrotask(() => validate(defaultValue));
  }, [defaultValue, validate]);

  const onChange = useCallback(
    ({ target }: { target: HTMLInputElement }) => {
      //TODO Check all the Input Type here and Implement validation

      let parsedValue: string | boolean | any[] | File | null =
        target.className.includes("checkboxGroupItem") ? [] : "";

      switch (target.type) {
        case "checkbox":
          if (target.className.includes("checkboxGroupItem")) {
            if (Array.isArray(value)) {
              if (target.checked && !value.includes(target.value)) {
                parsedValue =
                  Array.isArray(value) && value.concat(target.value);
              } else if (!target.checked && value.includes(target.value)) {
                parsedValue = value.filter((item) => item !== target.value);
              }
            }
            break;
          }
          parsedValue = target.checked;
          break;
        case "file": {
          // Use the actual File object, never target.value (browsers mask
          // the real path as "C:\fakepath\<filename>" which breaks regex
          // validation and left Continue permanently disabled).
          const file = target.files && target.files.length > 0 ? target.files[0] : null;
          parsedValue = file;
          validateFile(file);
          setValue(parsedValue);
          return;
        }
        default:
          parsedValue = target.value;
          break;
      }
      if (target.className.includes("selectItem")) {
        parsedValue = target.id;
      }
      if (
        typeof parsedValue === "string" &&
        target.className.includes("input_blurred")
      ) {
        validate(parsedValue);
      }
      setValue(parsedValue);
    },
    [validate, validateFile, value],
  );

  const onBlur = useCallback(() => {
    if (typeof value === "string") validate(value);
  }, [validate, value]);

  //TODO find a better way to control the checkbox element
  return (
    <div className={className}>
      {React.cloneElement(children, {
        label,
        value,
        message,
        onChange,
        onBlur,
        intent,
        checked: typeof value === "boolean" ? value : false,
        required,
        ...children.props,
      })}
    </div>
  );
}
